import { round } from 'lodash';
import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { getNullSelectOption } from '../../../../shared/form/inputs/Select';
import { Table, TBody, Td, Th, THead, Tr } from '../../../../shared/Table';
import { colourGreen09, colourNeutral09, colourRed09 } from '../../../../styling/colours';
import { spacing01, spacing02 } from '../../../../styling/spacing';
import { getLetterOrdinalAndLocationForManifold } from '../../../pdf/helpers/manifoldHelpers';
import { HeatSourceFormModel } from '../GetDataForQuoteOrDesignForm';
import { ManifoldFormModel } from '../manifolds/manifoldFormModel';
import { QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import { HeatSourceMapNode, ManifoldMapNode, MapNode, MapNodeType } from './mapNode';

type Props = {
  map: Array<MapNode>;
  manifolds: Array<ManifoldFormModel>;
};

const displayValue = (value: number | null | undefined): string => {
  return value ? round(value, 1).toString() : '';
};

const displayPercentage = (percentage: number | null | undefined): string => {
  return percentage ? `${round(percentage)}%` : '%';
};

const isMissingOrOutsideThreshold = (percentage: number | null | undefined): boolean => {
  return percentage === null || percentage === undefined || percentage > 66 || percentage < 0;
};
export const MapStats = ({ map, manifolds }: Props) => {
  const heatSource = map.find((node): node is HeatSourceMapNode => node.type === MapNodeType.HeatSource);
  const { referenceData } = assertNotNull(useContext(QuoteOrDesignFormContext));

  const currentHeatSource =
    referenceData.heatSourceOptions.find(
      heatSourceOption => heatSourceOption.value?.displayName === heatSource?.data.heatSource,
    )?.value ?? getNullSelectOption<HeatSourceFormModel>().value;
  const isHeatPump = currentHeatSource?.isHeatPump;
  const manifoldNames = Object.fromEntries(
    manifolds.map((manifold, index) => [index, getLetterOrdinalAndLocationForManifold(manifold.name)[0]]),
  );
  const manifoldNodes = map
    .map(node =>
      node.type === MapNodeType.Manifold
        ? { node, name: node.data.manifoldIndex !== null ? manifoldNames[node.data.manifoldIndex] : '' }
        : null,
    )
    .filter((node): node is { node: ManifoldMapNode; name: string } => node !== null);
  return (
    <DataTable>
      <THead>
        <Tr>
          <NoPaddingTh colSpan={2} />
          <NoPaddingTh colSpan={3}>Boiler to BV(s)</NoPaddingTh>
          <NoPaddingTh colSpan={3}>UFH Pump(s)</NoPaddingTh>
        </Tr>
        <Tr>
          <VerticalTh />
          <VerticalTh>PM1?</VerticalTh>
          <VerticalTh>Req kPa</VerticalTh>
          <VerticalTh>Available kPa</VerticalTh>
          <VerticalTh>Pump Capacity</VerticalTh>
          <VerticalTh>Req kPa</VerticalTh>
          <VerticalTh>Available kPa</VerticalTh>
          <VerticalTh>Pump Capacity</VerticalTh>
        </Tr>
      </THead>
      <TBody>
        {manifoldNodes.map(({ node, name }) => (
          <Tr key={node.id}>
            <NoPaddingTd>{name}</NoPaddingTd>
            <NoPaddingTd>{node?.data?.pumpOnManifold ? 'Y' : 'N'}</NoPaddingTd>
            {!isHeatPump ? (
              <>
                <NoPaddingTd>
                  {displayValue(node?.data?.boilerToBlendingValve?.requiredPressureInKilopascals)}
                </NoPaddingTd>
                <NoPaddingTd>
                  {displayValue(node?.data?.boilerToBlendingValve?.availablePressureInKilopascals)}
                </NoPaddingTd>
                <IndicatorTd
                  $hasError={isMissingOrOutsideThreshold(node?.data.boilerToBlendingValve?.pumpCapacityPercentage)}
                >
                  {displayPercentage(node?.data?.boilerToBlendingValve?.pumpCapacityPercentage)}
                </IndicatorTd>
              </>
            ) : (
              <>
                <NoPaddingTd />
                <NoPaddingTd />
                <NoPaddingTd />
              </>
            )}
            <NoPaddingTd>{displayValue(node?.data?.pumpToUfh?.requiredPressureInKilopascals)}</NoPaddingTd>
            <NoPaddingTd>{displayValue(node?.data?.pumpToUfh?.availablePressureInKilopascals)}</NoPaddingTd>
            <IndicatorTd $hasError={isMissingOrOutsideThreshold(node?.data.pumpToUfh?.pumpCapacityPercentage)}>
              {displayPercentage(node?.data?.pumpToUfh?.pumpCapacityPercentage)}
            </IndicatorTd>
          </Tr>
        ))}
      </TBody>
    </DataTable>
  );
};

const DataTable = styled(Table)`
  height: fit-content;
  table-layout: fixed;
  width: 23%;
  border-collapse: collapse;
  margin-right: ${spacing02};
`;

const NoPaddingTh = styled(Th)`
  padding: ${spacing01};
  border: 1px solid ${colourNeutral09};
  text-align: center;
`;

const VerticalTh = styled(NoPaddingTh)`
  height: auto;
  writing-mode: vertical-rl;
  text-orientation: sideways;
  border: 1px solid ${colourNeutral09};
`;

const NoPaddingTd = styled(Td)`
  padding: ${spacing01};
  word-wrap: break-word;
  text-align: center;
  border: 1px solid ${colourNeutral09};
`;

const IndicatorTd = styled(NoPaddingTd)<{ $hasError: boolean | null }>`
  background-color: ${props => (props.$hasError != null ? (props.$hasError ? colourRed09 : colourGreen09) : 'inherit')};
`;
